import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { Search } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pagination } from "rsuite";
import {
  ExpertVerification,
  ExpertVerificationStatus,
  User,
} from "../../../../../models/user";
import { getSpecializationLabelKey } from "../../../../../models/specializationConstants";
import { AxiosHttpClient } from "../../../../../services/AxiosHttpService";
import {
  ExpertReviewAction,
  ExpertVerificationService,
} from "../../../../../services/ExpertVerificationService";
import { getUserFullName } from "../../../../Reusable/Utils";

const STATUS_FILTERS: ExpertVerificationStatus[] = [
  "NOT_SUBMITTED",
  "PENDING",
  "VERIFIED",
  "REJECTED",
  "SUSPENDED",
];

const statusColor = (
  status: ExpertVerificationStatus
): "default" | "warning" | "success" | "error" => {
  switch (status) {
    case "VERIFIED":
      return "success";
    case "PENDING":
      return "warning";
    case "REJECTED":
    case "SUSPENDED":
      return "error";
    default:
      return "default";
  }
};

const ExpertManagement: React.FC = () => {
  const { t } = useTranslation();

  const [experts, setExperts] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ExpertVerificationStatus | "">(
    ""
  );
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;

  const [selectedExpert, setSelectedExpert] = useState<User | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const loadExperts = () => {
    setLoading(true);
    let criteriaList = "roles=EXPERT";
    if (statusFilter) {
      criteriaList += `,expertDetails_verificationStatus=${statusFilter}`;
    }
    AxiosHttpClient.get<any>("api/v1/docuveritas/users/page", {
      criteriaList,
      pageNumber: 0,
      pageSize: 500,
      order: "desc",
    })
      .then((res) => {
        setExperts(res.content || []);
      })
      .catch(() => setExperts([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadExperts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  // Recherche client par nom / email / profession sur l'ensemble des experts.
  const filteredExperts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return experts;
    return experts.filter((e) => {
      const name = getUserFullName(e)?.toLowerCase() ?? "";
      const email = e.email?.toLowerCase() ?? "";
      const profession = e.expertDetails?.professionalTitle?.toLowerCase() ?? "";
      return (
        name.includes(q) || email.includes(q) || profession.includes(q)
      );
    });
  }, [experts, search]);

  const pagedExperts = filteredExperts.slice(
    (page - 1) * limit,
    page * limit
  );

  const handleReviewed = (message: string) => {
    setSnackbar({ open: true, message, severity: "success" });
    setSelectedExpert(null);
    loadExperts();
  };

  return (
    <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
      <Typography
        variant="h5"
        sx={{ fontWeight: 700, mb: 1, color: "hsl(var(--foreground))" }}
      >
        {t("EXPERT_MANAGEMENT_TITLE")}
      </Typography>
      <Typography
        variant="body2"
        sx={{ mb: 3, color: "hsl(var(--muted-foreground))" }}
      >
        {t("EXPERT_MANAGEMENT_SUBTITLE")}
      </Typography>

      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <Box
          sx={{
            position: "relative",
            flex: 1,
            minWidth: 240,
            display: "flex",
            alignItems: "center",
            borderRadius: "6px",
            p: "10px",
            height: "40px",
            border: "1px solid hsl(var(--border))",
            backgroundColor: "hsl(var(--background))",
          }}
        >
          <Search
            style={{
              height: 16,
              width: 16,
              marginRight: 8,
              color: "hsl(var(--muted-foreground))",
            }}
          />
          <TextField
            variant="standard"
            fullWidth
            placeholder={t("EXPERT_MANAGEMENT_SEARCH_PLACEHOLDER")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            InputProps={{ disableUnderline: true }}
          />
        </Box>

        <TextField
          select
          size="small"
          label={t("EXPERT_MANAGEMENT_STATUS_FILTER")}
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value as ExpertVerificationStatus | "")
          }
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">{t("EXPERT_MANAGEMENT_STATUS_ALL")}</MenuItem>
          {STATUS_FILTERS.map((s) => (
            <MenuItem key={s} value={s}>
              {t(`EXPERT_VERIFICATION_STATUS_${s}`)}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("EXPERT_MANAGEMENT_COL_EXPERT")}</TableCell>
                <TableCell>{t("EXPERT_MANAGEMENT_COL_EMAIL")}</TableCell>
                <TableCell>{t("EXPERT_MANAGEMENT_COL_PROFESSION")}</TableCell>
                <TableCell>{t("EXPERT_MANAGEMENT_COL_REGISTERED")}</TableCell>
                <TableCell>{t("EXPERT_MANAGEMENT_COL_STATUS")}</TableCell>
                <TableCell align="right">
                  {t("EXPERT_MANAGEMENT_COL_ACTIONS")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedExperts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    {t("EXPERT_MANAGEMENT_NO_RESULTS")}
                  </TableCell>
                </TableRow>
              ) : (
                pagedExperts.map((expert) => {
                  const status =
                    expert.expertDetails?.verificationStatus ?? "NOT_SUBMITTED";
                  return (
                    <TableRow key={expert.id} hover>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Avatar
                            src={expert.imageUrl}
                            sx={{ width: 32, height: 32 }}
                          />
                          {getUserFullName(expert)}
                        </Box>
                      </TableCell>
                      <TableCell>{expert.email}</TableCell>
                      <TableCell>
                        {expert.expertDetails?.professionalTitle || "-"}
                      </TableCell>
                      <TableCell>
                        {expert.createdAt
                          ? dayjs(expert.createdAt).format("DD/MM/YYYY")
                          : "-"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={t(`EXPERT_VERIFICATION_STATUS_${status}`)}
                          color={statusColor(status)}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setSelectedExpert(expert)}
                        >
                          {t("EXPERT_MANAGEMENT_OPEN")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>

          <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
            <Pagination
              prev
              next
              size="md"
              total={filteredExperts.length}
              limit={limit}
              activePage={page}
              onChangePage={setPage}
            />
          </Box>
        </>
      )}

      {selectedExpert && (
        <ExpertDetailDialog
          expert={selectedExpert}
          onClose={() => setSelectedExpert(null)}
          onReviewed={handleReviewed}
          onError={(message) =>
            setSnackbar({ open: true, message, severity: "error" })
          }
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

interface ExpertDetailDialogProps {
  expert: User;
  onClose: () => void;
  onReviewed: (message: string) => void;
  onError: (message: string) => void;
}

const ExpertDetailDialog: React.FC<ExpertDetailDialogProps> = ({
  expert,
  onClose,
  onReviewed,
  onError,
}) => {
  const { t } = useTranslation();

  const [verification, setVerification] = useState<ExpertVerification | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setLoading(true);
    ExpertVerificationService.getVerificationForAdmin(expert.id)
      .then((res) => {
        setVerification(res);
        setReason(res.reason ?? "");
      })
      .catch(() => onError(t("EXPERT_MANAGEMENT_LOAD_ERROR")))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expert.id]);

  const openDocument = async (documentId: string, download: boolean) => {
    try {
      const url = await ExpertVerificationService.getDocumentObjectUrl(
        documentId
      );
      if (download) {
        const link = document.createElement("a");
        link.href = url;
        link.download = "";
        link.click();
      } else {
        window.open(url, "_blank");
      }
    } catch {
      onError(t("EXPERT_MANAGEMENT_DOCUMENT_ERROR"));
    }
  };

  const review = (action: ExpertReviewAction) => {
    if ((action === "REJECT" || action === "SUSPEND") && !reason.trim()) {
      onError(t("EXPERT_MANAGEMENT_REASON_REQUIRED"));
      return;
    }
    setSubmitting(true);
    ExpertVerificationService.review(expert.id, action, reason.trim() || undefined)
      .then(() => onReviewed(t(`EXPERT_MANAGEMENT_${action}_SUCCESS`)))
      .catch(() => onError(t("EXPERT_MANAGEMENT_REVIEW_ERROR")))
      .finally(() => setSubmitting(false));
  };

  const status = verification?.status ?? "NOT_SUBMITTED";

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        {getUserFullName(expert)}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Informations générales */}
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              {t("EXPERT_MANAGEMENT_GENERAL_INFO")}
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 2 }}>
              <InfoRow label={t("EXPERT_MANAGEMENT_COL_EMAIL")} value={expert.email} />
              <InfoRow
                label={t("EXPERT_MANAGEMENT_COL_PROFESSION")}
                value={expert.expertDetails?.professionalTitle || "-"}
              />
              <InfoRow
                label={t("EXPERT_MANAGEMENT_COL_REGISTERED")}
                value={
                  expert.createdAt
                    ? dayjs(expert.createdAt).format("DD/MM/YYYY")
                    : "-"
                }
              />
              <InfoRow
                label={t("EXPERT_MANAGEMENT_SPECIALTIES")}
                value={
                  expert.expertDetails?.specialties
                    ?.map((s) => t(getSpecializationLabelKey(s)))
                    .join(", ") || "-"
                }
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <Typography variant="body2">
                {t("EXPERT_MANAGEMENT_COL_STATUS")}:
              </Typography>
              <Chip
                size="small"
                label={t(`EXPERT_VERIFICATION_STATUS_${status}`)}
                color={statusColor(status)}
              />
            </Box>

            {verification?.reason && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t("EXPERT_MANAGEMENT_LAST_REASON")}: {verification.reason}
              </Alert>
            )}

            {/* Documents justificatifs */}
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              {t("EXPERT_MANAGEMENT_DOCUMENTS")}
            </Typography>
            {verification?.documents?.length ? (
              verification.documents.map((doc) => (
                <Box
                  key={doc.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "6px",
                    p: 1,
                    mb: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <DescriptionIcon fontSize="small" />
                    <Typography variant="body2">{doc.fileName}</Typography>
                  </Box>
                  <Box>
                    <IconButton
                      size="small"
                      title={t("EXPERT_MANAGEMENT_PREVIEW")}
                      onClick={() => openDocument(doc.id, false)}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      title={t("EXPERT_MANAGEMENT_DOWNLOAD")}
                      onClick={() => openDocument(doc.id, true)}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))
            ) : (
              <Typography
                variant="body2"
                sx={{ color: "hsl(var(--muted-foreground))", mb: 2 }}
              >
                {t("EXPERT_MANAGEMENT_NO_DOCUMENTS")}
              </Typography>
            )}

            {/* Motif (obligatoire pour refus / suspension) */}
            <TextField
              label={t("EXPERT_MANAGEMENT_REASON_LABEL")}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              sx={{ mt: 2 }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          color="success"
          variant="contained"
          disabled={submitting || loading}
          onClick={() => review("VERIFY")}
        >
          {t("EXPERT_MANAGEMENT_VERIFY")}
        </Button>
        <Button
          color="error"
          variant="outlined"
          disabled={submitting || loading}
          onClick={() => review("REJECT")}
        >
          {t("EXPERT_MANAGEMENT_REJECT")}
        </Button>
        <Button
          color="warning"
          variant="outlined"
          disabled={submitting || loading}
          onClick={() => review("SUSPEND")}
        >
          {t("EXPERT_MANAGEMENT_SUSPEND")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const InfoRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Box>
    <Typography variant="caption" sx={{ color: "hsl(var(--muted-foreground))" }}>
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Box>
);

export default ExpertManagement;
